export const getBackendUri = () => import.meta.env.VITE_BACKEND_URI ?? "";

const BACKEND_URI = getBackendUri();

import { ChatAppResponse, ChatAppResponseOrError, ChatAppRequest, Config, SimpleAPIResponse, HistoryListApiResponse, HistoryApiResponse } from "./models";

export function getHeaders(idToken: string | undefined): Record<string, string> {
    if (idToken) {
        return { Authorization: `Bearer ${idToken}` };
    }

    return {};
}

export async function configApi(): Promise<Config> {
    const response = await fetch(`${BACKEND_URI}/config`, {
        method: "GET"
    });

    return (await response.json()) as Config;
}

export async function askApi(request: ChatAppRequest, token: string | undefined): Promise<ChatAppResponse> {
    // This function sends a request to the backend API to get an answer to a question.
    // It constructs the URL, sets the headers, and includes the request body.
    // If the request fails, it throws an error.
    // If the request is successful, it returns the parsed response.

    const headers = getHeaders(token);
    const response = await fetch(`${BACKEND_URI}/ask`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(request)
    });

    if (response.status > 299 || !response.ok) {
        throw Error(`Request failed with status ${response.status}`);
    }
    const parsedResponse: ChatAppResponseOrError = await response.json();
    if (parsedResponse.error) {
        throw Error(parsedResponse.error);
    }

    return parsedResponse as ChatAppResponse;
}

export async function chatApi(request: ChatAppRequest, shouldStream: boolean, token: string | undefined): Promise<Response> {
    // This function sends a chat request to the backend API.
    // It constructs the URL based on whether streaming is enabled.
    // It includes the necessary headers and request body.
    // If the request fails, it throws an error.
    // If the request is successful, it returns the response object.

    let url = `${BACKEND_URI}/chat`;
    if (shouldStream) {
        url += "/stream";
    }
    const headers = getHeaders(token);
    return await fetch(url, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(request)
    });
}

export async function addCommentApi(
    username: string,
    question: string,
    answerText: string,
    comment: string,
    rating: number,
    retrieveCount: any,
    retrievalMode: string,
    categories: string[],
    language: string
): Promise<Response> {
    // This function adds a comment to the PostgreSQL database.
    // It sends a POST request to the /add-comment endpoint with the comment data.
    // If the request fails, it throws an error.

    const url = `${BACKEND_URI}/add-comment`; // API endpoint for adding comments

    return await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            username,
            question,
            answer_text: answerText,
            comment,
            rating,
            retrieveCount,
            retrievalMode,
            categories,
            language
        })
    });
}

export async function addChatHistoryPostgresqlApi(
    username: string,
    question: string,
    answerText: string,
    retrieveCount: any,
    retrievalMode: string,
    model: string,
    categories: string[]
): Promise<Response> {
    // This function adds a chat history entry to the PostgreSQL database.
    // It sends a POST request to the /chat_history_postgresql endpoint with the chat history data.
    // If the request fails, it throws an error.

    const url = `${BACKEND_URI}/chat_history_postgresql`; // API endpoint for adding comments

    return await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            username,
            question,
            answer_text: answerText,
            retrieveCount,
            retrievalMode,
            model,
            categories
        })
    });
}

export async function getSpeechApi(text: string): Promise<string | null> {
    return await fetch("/speech", {
        method: "POST",
        headers: {
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            text: text
        })
    })
        .then(response => {
            if (response.status == 200) {
                return response.blob();
            } else if (response.status == 400) {
                console.log("Speech synthesis is not enabled.");
                return null;
            } else {
                console.error("Unable to get speech synthesis.");
                return null;
            }
        })
        .then(blob => (blob ? URL.createObjectURL(blob) : null));
}

export function getCitationFilePath(citation: string): string {
    // This function constructs the file path for a given citation.
    // It takes a citation string as input and returns a string representing the file path.
    // The file path is constructed by appending the citation to the base URL for content.
    // For example, if the citation is "file1.txt", the function will return "/content/file1.txt".
    return `${BACKEND_URI}/content/${citation}`;
}

export async function uploadFileApi(request: FormData, token: string): Promise<SimpleAPIResponse> {
    // It sends a POST request to the /upload endpoint with the file data in the request body.
    // If the request fails, it throws an error.

    const response = await fetch("/upload", {
        method: "POST",
        headers: getHeaders(token),
        body: request
    });

    if (!response.ok) {
        throw new Error(`Uploading files failed: ${response.statusText}`);
    }

    const dataResponse: SimpleAPIResponse = await response.json();
    return dataResponse;
}

export async function deleteUploadedFileApi(filename: string, token: string): Promise<SimpleAPIResponse> {
    // It sends a POST request to the /delete_uploaded endpoint with the filename in the request body.
    // If the request fails, it throws an error.

    const headers = getHeaders(token);
    const response = await fetch("/delete_uploaded", {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ filename })
    });

    if (!response.ok) {
        throw new Error(`Deleting file failed: ${response.statusText}`);
    }

    const dataResponse: SimpleAPIResponse = await response.json();
    return dataResponse;
}

export async function listUploadedFilesApi(token: string): Promise<string[]> {
    // It sends a GET request to the /list_uploaded endpoint.
    // If the request fails, it throws an error.

    const response = await fetch(`/list_uploaded`, {
        method: "GET",
        headers: getHeaders(token)
    });

    if (!response.ok) {
        throw new Error(`Listing files failed: ${response.statusText}`);
    }

    const dataResponse: string[] = await response.json();
    return dataResponse;
}

export async function postChatHistoryApi(item: any, token: string): Promise<any> {
    // It sends a POST request to the /chat_history endpoint with the item in the request body.
    // If the request fails, it throws an error.

    const headers = getHeaders(token);
    const response = await fetch("/chat_history", {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(item)
    });

    if (!response.ok) {
        throw new Error(`Posting chat history failed: ${response.statusText}`);
    }

    const dataResponse: any = await response.json();
    return dataResponse;
}

export async function getChatHistoryListApi(count: number, continuationToken: string | undefined, token: string): Promise<HistoryListApiResponse> {
    // It sends a GET request to the /chat_history/sessions endpoint with optional query parameters for count and continuationToken.
    // If the request fails, it throws an error.

    const headers = getHeaders(token);
    let url = `${BACKEND_URI}/chat_history/sessions?count=${count}`;
    if (continuationToken) {
        url += `&continuationToken=${continuationToken}`;
    }

    const response = await fetch(url.toString(), {
        method: "GET",
        headers: { ...headers, "Content-Type": "application/json" }
    });

    if (!response.ok) {
        throw new Error(`Getting chat histories failed: ${response.statusText}`);
    }

    const dataResponse: HistoryListApiResponse = await response.json();
    return dataResponse;
}

export async function getChatHistoryApi(id: string, token: string): Promise<HistoryApiResponse> {
    // It sends a GET request to the /chat_history/sessions/{id} endpoint.
    // If the request fails, it throws an error.

    const headers = getHeaders(token);
    const response = await fetch(`/chat_history/sessions/${id}`, {
        method: "GET",
        headers: { ...headers, "Content-Type": "application/json" }
    });

    if (!response.ok) {
        throw new Error(`Getting chat history failed: ${response.statusText}`);
    }

    const dataResponse: HistoryApiResponse = await response.json();
    return dataResponse;
}

export async function deleteChatHistoryApi(id: string, token: string): Promise<any> {
    // This function deletes a chat history item by its ID.
    // It sends a DELETE request to the /chat_history/sessions/{id} endpoint.
    // If the request fails, it throws an error.

    const headers = getHeaders(token);
    const response = await fetch(`/chat_history/sessions/${id}`, {
        method: "DELETE",
        headers: { ...headers, "Content-Type": "application/json" }
    });

    if (!response.ok) {
        throw new Error(`Deleting chat history failed: ${response.statusText}`);
    }
}

export async function getAllowedUsersFromPostgresqlApi(): Promise<string[]> {
    // This function fetches the list of allowed users from the backend.
    // It sends a GET request to the /allowed_users_postgresql endpoint and returns the list of users.
    // If the request fails, it throws an error.

    const url = `${BACKEND_URI}/allowed_users_postgresql`;
    const response = await fetch(url, {
        method: "GET",
        headers: { "Content-Type": "application/json" }
    });

    if (!response.ok) {
        throw new Error(`Getting allowed users failed: ${response.statusText}`);
    }

    const dataResponse: string[] = await response.json();
    return dataResponse;
}

export async function loginLocalApi(
    username: string,
    password: string
): Promise<{ token?: string; mfa_required?: boolean; username?: string; mfa_setup_required?: boolean; setup_token?: string }> {
    const response = await fetch(`${BACKEND_URI}/login/local`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password })
    });

    const parsedResponse = await response.json();
    if (response.status > 299 || !response.ok) {
        throw new Error(parsedResponse.error || "Unknown error");
    }
    return parsedResponse;
}

export async function verifyMfaApi(username: string, totp_code: string): Promise<{ token: string }> {
    const response = await fetch(`${BACKEND_URI}/login/local/verify-mfa`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, totp_code })
    });

    const parsedResponse = await response.json();
    if (response.status > 299 || !response.ok) {
        throw new Error(parsedResponse.error || "Unknown error");
    }
    return parsedResponse;
}

export async function setupMfaApi(token: string): Promise<Blob> {
    const response = await fetch(`${BACKEND_URI}/mfa/setup`, {
        method: "POST",
        headers: { ...getHeaders(token) }
    });

    if (!response.ok) {
        const parsedResponse = await response.json();
        throw new Error(parsedResponse.error || "Failed to set up MFA");
    }
    return response.blob();
}

export async function verifyMfaSetupApi(token: string, totp_code: string): Promise<{ message: string; token?: string }> {
    const response = await fetch(`${BACKEND_URI}/mfa/verify`, {
        method: "POST",
        headers: { ...getHeaders(token), "Content-Type": "application/json" },
        body: JSON.stringify({ totp_code })
    });

    const parsedResponse = await response.json();
    if (!response.ok) {
        throw new Error(parsedResponse.error || "Failed to verify MFA code");
    }
    return parsedResponse;
}

export async function changePasswordApi(token: string, old_password: string, new_password: string): Promise<{ message: string }> {
    const response = await fetch(`${BACKEND_URI}/user/change-password`, {
        method: "POST",
        headers: { ...getHeaders(token), "Content-Type": "application/json" },
        body: JSON.stringify({ old_password, new_password })
    });

    const parsedResponse = await response.json();
    if (!response.ok) {
        throw new Error(parsedResponse.error || "Failed to change password");
    }
    return parsedResponse;
}

/**
 * Fetches example questions for the given categories from the backend,
 * shuffles them, and returns up to 3 random examples.
 * @param categories Optional list of category names to fetch examples for. If undefined or empty, fetches all.
 * @returns A promise that resolves to an array of up to 3 random example questions.
 */
export async function getCategoryExamplesApi(categories?: string[]): Promise<string[]> {
    try {
        let url = `${BACKEND_URI}/api/examples`; // Base URL for the examples endpoint

        // If categories are provided and the list is not empty, join them into a comma-separated string
        // and add as a query parameter. Fetch only examples for these categories.
        if (categories && categories.length > 0) {
            url += `?categories=${encodeURIComponent(categories.join(","))}`;
        }
        // Otherwise, the backend will return examples for all categories it has.

        const response = await fetch(url, {
            method: "GET",
            headers: {
                "Content-Type": "application/json"
                // Add authentication headers if needed, similar to getHeaders()
                // headers: await getHeaders(idToken) // Example if auth is needed
            }
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch category examples: ${response.status} ${response.statusText}`);
        }

        const examplesByCategory: Record<string, string[]> = await response.json();

        // Flatten all fetched examples into a single list
        const allExamples = Object.values(examplesByCategory).flat();

        // Shuffle the flattened list randomly and take the first 3
        const shuffledExamples = allExamples.sort(() => 0.5 - Math.random());
        return shuffledExamples.slice(0, 3);
    } catch (error) {
        console.error("Error fetching or processing category examples:", error);
        // Return an empty array or default examples in case of an error
        return []; // Return empty array on error
    }
}

export async function getParameterValueApi(parameterName: string): Promise<number | string> {
    try {
        // Construct the URL with the query parameter
        const response = await fetch(`${BACKEND_URI}/api/parameters/getParameter?name=${encodeURIComponent(parameterName)}`);
        if (!response.ok) {
            console.error(`Failed to fetch parameter '${parameterName}':`, response.statusText);
            // Fallback to a default value if the API call fails
            if (parameterName === "retrieveCount") {
                return 30;
            }
            return 0; // Default for other parameters or if not found
        }
        const data = await response.json();
        // The backend returns a JSON like { "value": "some_value" } or { "value": null }
        if (typeof data.value === "number" || typeof data.value === "string") {
            return data.value;
        }
        console.warn(`Invalid format for parameter '${parameterName}', using default.`);
        return parameterName === "retrieveCount" ? 30 : 0;
    } catch (error) {
        console.error(`Error fetching parameter '${parameterName}':`, error);
        if (parameterName === "retrieveCount") {
            return 30;
        }
        return 0; // Default value in case of network error etc.
    }
}

export async function getUserCategoriesFromPostgresqlApi(username: string, token: string | undefined): Promise<string[]> {
    // This function fetches the list of user categories from the backend.
    // It constructs the URL with the username as a query parameter, sends a GET request,
    // and returns the list of categories as an array of strings.
    // If the request fails, it throws an error.
    username = username.toLowerCase();
    const url = `${BACKEND_URI}/user_categories_postgresql?username=${encodeURIComponent(username)}`;
    const headers = getHeaders(token);
    const response = await fetch(url, {
        method: "GET",
        headers: { ...headers, "Content-Type": "application/json" }
    });

    if (!response.ok) {
        throw new Error(`Getting categories failed: ${response.statusText}`);
    }

    const dataResponse = await response.json();
    // The API now returns a UserInfo object { username, isDev, can_manage_documents, categories, categories_google }
    // We only need the Azure AI Search categories array here
    return dataResponse.categories || [];
}

export interface UserInfo {
    username: string;
    isDev: boolean;
    can_manage_documents: boolean;
    categories: string[]; // Catégories Azure AI Search
    categories_google: string[]; // Catégories Google RAG (FileSearch)
}

/**
 * Fetches the full user info object from the backend, including both
 * Azure AI Search categories (categories) and Google RAG categories (categories_google).
 */
export async function getUserInfoFromPostgresqlApi(token: string | undefined): Promise<UserInfo | null> {
    const url = `${BACKEND_URI}/user_categories_postgresql`;
    const headers = getHeaders(token);
    const response = await fetch(url, {
        method: "GET",
        headers: { ...headers, "Content-Type": "application/json" }
    });

    if (!response.ok) {
        return null;
    }

    const data = await response.json();
    return {
        username: data.username || "",
        isDev: data.isDev || false,
        can_manage_documents: data.can_manage_documents || false,
        categories: data.categories || [],
        categories_google: data.categories_google || []
    };
}

export async function getFilesForCategory(category: string, token: string | undefined): Promise<string[]> {
    // This function fetches the list of files for a given category from the backend.
    // It constructs the URL with the category as a query parameter, sends a GET request,
    // and returns the list of file names as an array of strings.
    // If the request fails, it throws an error.

    const url = `${BACKEND_URI}/get_files_by_category?category=${encodeURIComponent(category)}`;
    const headers = getHeaders(token);
    const response = await fetch(url, {
        method: "GET",
        headers: { ...headers, "Content-Type": "application/json" }
    });

    if (!response.ok) {
        throw new Error(`Getting files for category ${category} failed: ${response.statusText}`);
    }

    const dataResponse: string[] = await response.json();
    return dataResponse;
}

export async function getDevUsersFromPostgresqlApi(): Promise<string[]> {
    // This function fetches the list of dev users from the backend.
    // It sends a GET request to the /dev_users_postgresql endpoint and returns the list of users.
    // If the request fails, it throws an error.

    const url = `${BACKEND_URI}/dev_users_postgresql`;
    const response = await fetch(url, {
        method: "GET",
        headers: { "Content-Type": "application/json" }
    });

    if (!response.ok) {
        throw new Error(`Getting dev users failed: ${response.statusText}`);
    }

    const dataResponse: string[] = await response.json();
    return dataResponse;
}
